import { formatDateTime } from '@batiflow/i18n';
import { ConfirmDialog, SkeletonList, isolate } from '@batiflow/ui';
import {
  Badge,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Regular,
  Clock16Regular,
  Prohibited16Regular,
  Warning16Regular,
} from '@fluentui/react-icons';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useNotify } from '@/shared/hooks/use-notify';
import { useRevokeShareLink, useShareLinks } from './api';
import {
  downloadsLeft,
  isLive,
  newestFirst,
  type ShareLink,
  type ShareLinkStatus,
  type ShareTarget,
} from './model';

const useStyles = __css({"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"entry":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"head":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r"},"title":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9","Bgto7am":"fuzgk9b","Bhrd7zp":"fl43uef","Btd35i7":"fjksvth"},"facts":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"f1xcgk68","Belr9w4":"f1j0q4x9","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb","Be2twd7":"fy9rknc","Bg96gwp":"fwrc4pm"},"muted":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"f11d4kpn"}});

const STATUS_ICON: Record<ShareLinkStatus, ReactElement> = {
  active: <CheckmarkCircle16Regular />,
  exhausted: <Warning16Regular />,
  expired: <Clock16Regular />,
  revoked: <Prohibited16Regular />,
};

const STATUS_COLOR: Record<ShareLinkStatus, 'success' | 'warning' | 'subtle' | 'danger'> = {
  active: 'success',
  exhausted: 'warning',
  expired: 'subtle',
  revoked: 'danger',
};

/** The status of a link in words and an icon, never by colour alone. */
export function StatusBadge({ status }: { status: ShareLinkStatus }) {
  const { t } = useTranslation('shareLinks');
  return (
    <Badge appearance="tint" color={STATUS_COLOR[status]} icon={STATUS_ICON[status]}>
      {t(`status.${status}`)}
    </Badge>
  );
}

export interface LinksListProps {
  spaceId: string;
  /** The document or folder whose links are listed; null lists every link of the space. */
  target: ShareTarget | null;
  /** Names the target on each line (the space list, where links of many items mix). */
  showTarget?: boolean;
  /** Said when there is no link. */
  emptyText: string;
  /** Accessible name of the list. */
  label: string;
}

/**
 * The links of an item or of a space: status (text and icon), expiry, downloads used and limit,
 * last access, and "Révoquer" with a confirmation (the link stops working at once).
 */
export function LinksList({
  spaceId,
  target,
  showTarget = false,
  emptyText,
  label,
}: LinksListProps) {
  const { t } = useTranslation('shareLinks');
  const styles = useStyles();
  const locale = useLocale();
  const notify = useNotify();
  const links = useShareLinks(spaceId, target);
  const revoke = useRevokeShareLink(spaceId);
  const [revoking, setRevoking] = useState<ShareLink | null>(null);

  if (links.isError) {
    return (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>{t('list.loadFailed')}</MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void links.refetch();
            }}
          >
            {t('common:actions.retry')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  }
  if (links.isPending) return <SkeletonList count={2} />;

  const items = [...links.data.items].sort(newestFirst);
  if (items.length === 0) return <p className={styles.muted}>{emptyText}</p>;

  const date = (value: string) => isolate(formatDateTime(value, locale));
  return (
    <>
      <ul className={styles.list} aria-label={label}>
        {items.map((link) => {
          const name = isolate(link.label ?? link.targetName);
          const left = downloadsLeft(link);
          return (
            <li key={link.id} className={styles.entry}>
              <div className={styles.head}>
                <span className={styles.title}>
                  <bdi>{link.label ?? t('list.unnamed')}</bdi>
                  <StatusBadge status={link.status} />
                </span>
                {isLive(link) ? (
                  <Button
                    size="small"
                    aria-label={t('list.revokeFor', { name })}
                    onClick={() => {
                      setRevoking(link);
                    }}
                  >
                    {t('list.revoke')}
                  </Button>
                ) : null}
              </div>
              <p className={styles.facts}>
                {showTarget ? (
                  <span>
                    {t(link.target.type === 'folder' ? 'list.folder' : 'list.document', {
                      name: isolate(link.targetName),
                    })}
                  </span>
                ) : null}
                <span>
                  {t(link.status === 'expired' ? 'list.expiredOn' : 'list.expiresOn', {
                    date: date(link.expiresAt),
                  })}
                </span>
                {link.revokedAt ? (
                  <span>{t('list.revokedOn', { date: date(link.revokedAt) })}</span>
                ) : null}
                <span>
                  {link.downloadAllowed
                    ? left === null
                      ? t('list.downloadsUnlimited', { used: link.downloads })
                      : t('list.downloadsLimited', { used: link.downloads, max: link.maxDownloads })
                    : t('list.downloadsOff')}
                </span>
                <span>
                  {link.lastAccessedAt
                    ? t('list.lastAccess', { date: date(link.lastAccessedAt) })
                    : t('list.neverOpened')}
                </span>
                {link.hasPassword ? <span>{t('list.password')}</span> : null}
                {link.watermark ? <span>{t('list.watermark')}</span> : null}
                <span>{t('list.createdBy', { name: isolate(link.createdBy.name) })}</span>
              </p>
            </li>
          );
        })}
      </ul>
      {revoking ? (
        <ConfirmDialog
          open
          destructive
          title={t('revoke.title', { name: isolate(revoking.label ?? revoking.targetName) })}
          body={t('revoke.body')}
          confirmLabel={t('revoke.confirm')}
          cancelLabel={t('common:actions.cancel')}
          onOpenChange={(next) => {
            if (!next) setRevoking(null);
          }}
          onConfirm={async () => {
            await revoke.mutateAsync(revoking.id);
            notify({ intent: 'success', message: t('revoke.done') });
          }}
          onError={() => {
            notify({ intent: 'error', message: t('revoke.failed') });
          }}
        />
      ) : null}
    </>
  );
}


import "/opt/batiflow/app/apps/web/src/features/share-links/links-list.griffel.css";